import { isArray, isPlainObject, set, truncate } from "lodash";
import diff from "microdiff";
import type { JSONObject, JSONValue } from "../models/fibo";

export const isObj = (v: unknown): v is JSONObject => v !== null && isPlainObject(v);

export const isArr = (v: unknown): v is JSONValue[] => isArray(v);

export const updateStructuredPromptValue = (data: JSONValue, path: string[], newValue: JSONValue): JSONValue => {
	if (!isObj(data)) return data;

	const result = JSON.parse(JSON.stringify(data)); // Deep clone
	set(result, path, newValue);
	return result;
};

export const trunc = (s: string, n: number) => truncate(s, { length: n, omission: "…" });

export function countKinds(value: JSONValue) {
	if (isArr(value)) return { obj: 0, arr: (value as unknown[]).length };

	if (isObj(value)) {
		return { obj: Object.keys(value).length, arr: 0 };
	}
	return { obj: 0, arr: 0 };
}

function normalizePair(a: JSONValue | undefined, b: JSONValue | undefined) {
	const left = a === undefined ? null : a;
	const right = b === undefined ? null : b;

	if (isObj(left) && right === null) return { left, right: {} };
	if (isObj(right) && left === null) return { left: {}, right };

	if (isArr(left) && right === null) return { left, right: [] };
	if (isArr(right) && left === null) return { left: [], right };

	return { left, right };
}

export function collectChanged(
	prev?: JSONValue,
	next?: JSONValue,
	opts?: { ignoreWhenPrevNull?: boolean },
): Set<string> {
	if (opts?.ignoreWhenPrevNull && (prev === null || prev === undefined)) {
		return new Set();
	}
	const { left, right } = normalizePair(prev, next);

	const out = new Set<string>();
	for (const c of diff(left as any[] | Record<string, any>, right as any[] | Record<string, any>)) {
		const p = c.path.map(String).join(".");
		if (p) out.add(p);

		if (p) {
			const parts = p.split(".");
			for (let i = parts.length - 1; i > 0; i--) {
				out.add(parts.slice(0, i).join("."));
			}
		}
	}

	if (left !== right && out.size === 0) out.add("");

	return out;
}

export function hasChangeUnder(changed: Set<string>, base: string) {
	if (!base) return changed.size > 0;
	if (changed.has(base)) return true;
	const needle = base + ".";
	for (const p of changed) if (p.startsWith(needle)) return true;
	return false;
}

export const hasChangeForAny = (changed: Set<string>, bases: string[]) => bases.some((b) => hasChangeUnder(changed, b));
